import React, { useEffect, useState } from "react";
import Layout from "./Layout";
import { defaultAxiosInstance } from "../api/defaultInstance";
import "../css/AdManagement.css";
import * as XLSX from "xlsx";
import {
  FaFileExcel,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaTrash,
} from "react-icons/fa";
import MemoModal from "../modules/campaign/components/MemoModal";
import { useMemoEdit } from "../modules/campaign/hooks/useMemoEdit";
import { saveInvoiceMemo } from "../modules/campaign/services/campaignService";
import "../modules/campaign/Campaign.css";

export function getExcelDateRange(filter) {
  if (filter.month) {
    const [year, month] = filter.month.split("-");
    const startDate = `${year}-${month}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const endDate = `${year}-${month}-${String(lastDay).padStart(2, "0")}`;
    return { startDate, endDate };
  }

  if (filter.startDate || filter.endDate) {
    return {
      startDate: filter.startDate || "",
      endDate: filter.endDate || "",
    };
  }

  if (filter.paymentDate) {
    return { startDate: filter.paymentDate, endDate: filter.paymentDate };
  }

  return null;
}

function InvoiceManagement() {
  const [isLoading, setIsLoading] = useState(false);
  const [isListDownloading, setIsListDownloading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({ totalCount: 0 });
  const today = new Date();
  const defaultMonth = today.toISOString().slice(0, 7);
  const [filter, setFilter] = useState({
    month: defaultMonth,
    issueDate: "",
    paymentDate: "",
    startDate: "",
    endDate: "",
    businessNo: "",
    page: 1,
    limit: 50,
    type: "tax", // tax: 세금계산서, receipt: 현금영수증
    receiptPurpose: "", // 0: 소득공제, 1: 지출증빙
    receiptIssueNumber: "", // 발급수단번호
    paymentType: "", // 추가: 결제 수단 필터 (account, v_bank)
    issueStatus: "",
  });
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  const memoEdit = useMemoEdit({
    saveFn: saveInvoiceMemo,
    onUpdate: (id, newMemo) => {
      setInvoices((prev) =>
        prev.map((inv) => (inv.id === id ? { ...inv, memo: newMemo } : inv)),
      );
    },
  });

  // 데이터 조회 함수
  const fetchInvoiceData = async (currentFilter = filter) => {
    setIsLoading(true);
    try {
      // 빈 문자열인 파라미터는 제외하고 전송 (백엔드 400 에러 방지)
      const params = Object.fromEntries(
        Object.entries(currentFilter).filter(([_, v]) => v !== ""),
      );

      const response = await defaultAxiosInstance.get("/invoice/list", {
        params,
      });
      const result = response.data.data;
      setInvoices(
        (result.list || []).map((inv) => ({
          ...inv,
          memo: inv.issueMemo ?? null,
        })),
      );
      setSummary({ totalCount: result.totalCount || 0 });
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceData();
  }, []);

  const { openModal: openMemoModal } = memoEdit;
  useEffect(() => {
    const handleMemoClick = (e) => {
      const button = e.target.closest('button[data-action="memo"]');
      if (button) {
        const id = Number(button.getAttribute("data-id"));
        const currentMemo = button.getAttribute("data-memo") || null;
        openMemoModal(id, currentMemo || null);
      }
    };
    document.addEventListener("click", handleMemoClick, true);
    return () => document.removeEventListener("click", handleMemoClick, true);
  }, [openMemoModal]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilter((prev) => {
      const newFilter = { ...prev, [name]: value };

      // 일단위 조회 필드 입력 시 월단위 필드 초기화
      if (name === "startDate" || name === "endDate") {
        if (value) newFilter.month = "";
      }
      // 월단위 필드 입력 시 일단위 조회 필드 초기화
      if (name === "month") {
        if (value) {
          newFilter.startDate = "";
          newFilter.endDate = "";
        }
      }

      return newFilter;
    });

    // 발행종류가 변경되면 자동으로 조회 실행
    if (name === "type") {
      setFilter((prev) => {
        const newFilter = { ...prev, [name]: value, page: 1 };
        setSelectedInvoices([]);
        fetchInvoiceData(newFilter);
        return newFilter;
      });
    }
  };

  const applyFilter = () => {
    const newFilter = { ...filter, page: 1 };
    setFilter(newFilter);
    setSelectedInvoices([]);
    fetchInvoiceData(newFilter);
  };

  const handleCheckboxChange = (id) => {
    setSelectedInvoices((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedInvoices(invoices.map((inv) => inv.id));
    } else {
      setSelectedInvoices([]);
    }
  };

  const handlePrevPage = () => {
    const newPage = Math.max(1, filter.page - 1);
    const newFilter = { ...filter, page: newPage };
    setFilter(newFilter);
    fetchInvoiceData(newFilter);
  };

  const handleNextPage = () => {
    const newPage = filter.page + 1;
    const newFilter = { ...filter, page: newPage };
    setFilter(newFilter);
    fetchInvoiceData(newFilter);
  };

  const handlePageClick = (page) => {
    const newFilter = { ...filter, page };
    setFilter(newFilter);
    fetchInvoiceData(newFilter);
  };

  // 금액 포맷팅 함수 추가
  const formatAmount = (amount) => {
    return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "원";
  };

  // 엑셀 내보내기 함수 수정
  const exportToExcel = async () => {
    setIsLoading(true);
    let dataToExport = [];

    try {
      if (selectedInvoices.length > 0) {
        // 1. 체크박스로 선택된 항목이 있는 경우
        dataToExport = invoices.filter((inv) =>
          selectedInvoices.includes(inv.id),
        );
      } else if (filter.startDate || filter.endDate) {
        // 2. 일단위 조회(시작일/종료일)인 경우: 전체 데이터 조회
        const response = await defaultAxiosInstance.get("/invoice/list", {
          params: { ...filter, page: 1, limit: 10000 }, // 충분히 큰 숫자로 전체 조회
        });
        dataToExport = response.data.data.list || [];
        if (dataToExport.length === 0) {
          alert("내보낼 데이터가 없습니다.");
          setIsLoading(false);
          return;
        }
      } else {
        // 3. 월단위 조회인 경우: 현재 페이지(최대 50건)만 내보냄
        dataToExport = invoices;
        if (dataToExport.length === 0) {
          alert("내보낼 데이터가 없습니다.");
          setIsLoading(false);
          return;
        }
      }

      const today = new Date();
      const yyyyMMdd = today.toISOString().slice(0, 10).replace(/-/g, "");

      if (filter.type === "tax") {
        // 세금계산서 엑셀 생성 로직 (기존과 동일)
        const sheetHeader = [
          "전자(세금)계산서 종류",
          "작성일자",
          "공급자 등록번호",
          "공급자 종사업장번호",
          "공급자 상호",
          "공급자 성명",
          "공급자 사업장주소",
          "공급자 업태",
          "공급자 종목",
          "공급자 이메일",
          "공급받는자 등록번호",
          "공급받는자 종사업장번호",
          "공급받는자 상호",
          "공급받는자 성명",
          "공급받는자 사업장주소",
          "공급받는자 업태",
          "공급받는자 종목",
          "공급받는자 이메일1",
          "공급받는자 이메일2",
          "공급가액합계",
          "세액합계",
          "비고",
          "일자1",
          "품목1",
          "규격1",
          "수량1",
          "단가1",
          "공급가액1",
          "세액1",
          "품목비고1",
          "일자2",
          "품목2",
          "규격2",
          "수량2",
          "단가2",
          "공급가액2",
          "세액2",
          "품목비고2",
          "일자3",
          "품목3",
          "규격3",
          "수량3",
          "단가3",
          "공급가액3",
          "세액3",
          "품목비고3",
          "일자4",
          "품목4",
          "규격4",
          "수량4",
          "단가4",
          "공급가액4",
          "세액4",
          "품목비고4",
          "현금",
          "수표",
          "어음",
          "외상미수금",
          "영수(01),청구(02)",
          "메모",
        ];
        const fixed = {
          supplierNo: "4688703126",
          supplierName: "주식회사 엠플리파이",
          supplierCeo: "전유상",
          supplierAddr: "서울특별시 금천구 디지털로10길 37, 537-540호",
          supplierBizType: "전문, 과학 및 기술서비스업",
          supplierBizItem: "광고 대행업",
          supplierEmail: "amplify15813@naver.com",
        };
        const rows = dataToExport.map((inv) => {
          const total = inv.totalAmount;
          const supply = Math.round(total / 1.1);
          const tax = total - supply;
          const day = inv.paymentDate.replace(/\./g, "").slice(-2);

          return [
            "01",
            "20" + inv.paymentDate.replace(/\./g, ""),
            fixed.supplierNo,
            "",
            fixed.supplierName,
            fixed.supplierCeo,
            fixed.supplierAddr,
            fixed.supplierBizType,
            fixed.supplierBizItem,
            fixed.supplierEmail,
            inv.businessNo,
            "",
            inv.companyName,
            inv.ownerName,
            "",
            "",
            "",
            inv.email || "",
            "",
            supply,
            tax,
            "0",
            day,
            "",
            "",
            "",
            "",
            supply,
            tax,
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "02",
            inv.memo || "",
          ];
        });
        const ws = XLSX.utils.aoa_to_sheet([sheetHeader, ...rows]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "계산서");
        XLSX.writeFile(wb, `invoices_${yyyyMMdd}.xlsx`);
      } else {
        // 현금영수증 엑셀 생성 로직 (기존과 동일)
        const sheetHeader = [
          "용도구분",
          "발급수단번호",
          "총거래금액(합계)",
          "공급가액",
          "부가가치세",
          "메모",
        ];
        const rows = dataToExport.map((inv) => {
          const total = inv.totalAmount;
          const supply = Math.round(total / 1.1);
          const tax = total - supply;
          return [
            inv.receiptPurpose,
            inv.receiptIssueNumber,
            total,
            supply,
            tax,
            inv.issueMemo || inv.memo || "",
          ];
        });
        const ws = XLSX.utils.aoa_to_sheet([sheetHeader, ...rows]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "현금영수증");
        XLSX.writeFile(wb, `receipts_${yyyyMMdd}.xlsx`);
      }
    } catch (error) {
      console.error("Export error:", error);
      alert("데이터를 가져오는 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 발행완료/취소발행 처리 (샘플)
  const handleIssueStatus = async (status) => {
    if (selectedInvoices.length === 0) {
      alert("선택된 항목이 없습니다.");
      return;
    }
    await defaultAxiosInstance.post(
      `/invoice/${status === 1 ? "issue" : "cancel"}`,
      { ids: selectedInvoices },
    );
    fetchInvoiceData();
    setSelectedInvoices([]);
  };

  // 삭제 처리 함수
  const handleDeleteClick = () => {
    if (selectedInvoices.length === 0) {
      alert("삭제할 항목을 선택해주세요.");
      return;
    }

    const selectedInvoiceItems = invoices.filter((invoice) =>
      selectedInvoices.includes(invoice.id),
    );

    const hasInvalidInvoice = selectedInvoiceItems.some((invoice) => {
      const userName = invoice.userName || "";
      return (
        !userName.includes("테스트") &&
        !userName.includes("전유상") &&
        !userName.includes("이령빈") &&
        !userName.includes("서한석") &&
        !userName.includes("null") &&
        !userName.includes("채영호") &&
        !userName.includes("매니저")
      );
    });

    if (hasInvalidInvoice) {
      alert("테스트 항목만 삭제할 수 있습니다.");
      return;
    }

    if (window.confirm("선택한 항목을 삭제하시겠습니까?")) {
      handleDeleteConfirm();
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsLoading(true);

      const selectedInvoiceItems = invoices.filter((invoice) =>
        selectedInvoices.includes(invoice.id),
      );

      const paymentTransactions = selectedInvoiceItems.map((invoice) => ({
        adPaymentTransactionId: invoice.id,
        userName: invoice.userName || "",
      }));

      await defaultAxiosInstance.delete(
        "/advertisement/admin/paymentTransactions",
        {
          data: { paymentTransactions },
        },
      );

      alert("삭제가 완료되었습니다.");
      setSelectedInvoices([]);
      fetchInvoiceData();
    } catch (error) {
      console.error("Error deleting invoices:", error);
      alert("삭제 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleListExcelDownload = async () => {
    const dateRange = getExcelDateRange(filter);
    if (!dateRange) {
      alert("날짜 조건(월, 시작일/종료일, 결제일자)을 먼저 설정해주세요.");
      return;
    }
    try {
      setIsListDownloading(true);
      const params = Object.fromEntries(
        Object.entries(filter).filter(([_, v]) => v !== ""),
      );
      const response = await defaultAxiosInstance.get("/invoice/list", {
        params: { ...params, page: 1, limit: 10000 },
      });
      const dataToExport = response.data.data.list || [];

      if (dataToExport.length === 0) {
        alert("내보낼 데이터가 없습니다.");
        return;
      }

      if (filter.type === "tax") {
        const sheetHeader = [
          "번호",
          "작성자",
          "결제일자",
          "사업자번호",
          "상호명",
          "대표자명",
          "공급가액",
          "세액",
          "합계액",
          "발행상태",
          "발행일자",
          "메모",
        ];
        const rows = dataToExport.map((inv, idx) => [
          idx + 1,
          inv.userName,
          inv.paymentDate,
          inv.businessNo,
          inv.companyName,
          inv.ownerName,
          inv.supplyAmount,
          inv.taxAmount,
          inv.totalAmount,
          getIssueStatusText(inv.issueStatus),
          inv.issueDate,
          inv.issueMemo || inv.memo || "",
        ]);
        const ws = XLSX.utils.aoa_to_sheet([sheetHeader, ...rows]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "계산서목록");
        XLSX.writeFile(
          wb,
          `계산서목록_${dateRange.startDate}_${dateRange.endDate}.xlsx`,
        );
      } else {
        const sheetHeader = [
          "번호",
          "작성자",
          "결제일자",
          "발급수단번호",
          "종류",
          "공급가액",
          "세액",
          "합계액",
          "발행상태",
          "발행일자",
          "메모",
        ];
        const rows = dataToExport.map((inv, idx) => [
          idx + 1,
          inv.userName,
          inv.paymentDate,
          inv.receiptIssueNumber,
          inv.receiptPurpose === "0" ? "소득공제" : "지출증빙",
          inv.supplyAmount,
          inv.taxAmount,
          inv.totalAmount,
          getIssueStatusText(inv.issueStatus),
          inv.issueDate,
          inv.issueMemo || inv.memo || "",
        ]);
        const ws = XLSX.utils.aoa_to_sheet([sheetHeader, ...rows]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "영수증목록");
        XLSX.writeFile(
          wb,
          `영수증목록_${dateRange.startDate}_${dateRange.endDate}.xlsx`,
        );
      }
    } catch (error) {
      console.error("List excel download error:", error);
      alert("엑셀 다운로드 중 오류가 발생했습니다.");
    } finally {
      setIsListDownloading(false);
    }
  };

  const startIndex = (filter.page - 1) * filter.limit + 1;
  const endIndex = Math.min(filter.page * filter.limit, summary.totalCount);

  // 발행 상태에 따른 스타일 클래스 반환 함수 추가
  const getIssueStatusClass = (status) => {
    switch (status) {
      case 0:
        return "badge-미접수";
      case 1:
        return "badge-접수완료";
      case 2:
        return "badge-수정발행";
      case -1:
        return "badge-종료";
      default:
        return "";
    }
  };

  // 발행 상태 텍스트 반환 함수 추가
  const getIssueStatusText = (status) => {
    switch (status) {
      case 0:
        return "발행전";
      case 1:
        return "발행완료";
      case 2:
        return "수정발행";
      case -1:
        return "취소발행";
      default:
        return "";
    }
  };

  return (
    <Layout>
      <div className="ad-management-container">
        <div className="filter-bar4">
          <div className="filter-row">
            <div className="filter-group">
              <label>시작일</label>
              <input
                type="date"
                name="startDate"
                onChange={handleFilterChange}
                value={filter.startDate}
              />
            </div>
            <div className="filter-group">
              <label>종료일</label>
              <input
                type="date"
                name="endDate"
                onChange={handleFilterChange}
                value={filter.endDate}
              />
            </div>
          </div>
          <div className="filter-row">
            <div className="filter-group">
              <label>월</label>
              <input
                type="month"
                name="month"
                onChange={handleFilterChange}
                value={filter.month}
              />
            </div>
            <div className="filter-group">
              <label>발행일자</label>
              <input
                type="date"
                name="issueDate"
                onChange={handleFilterChange}
                value={filter.issueDate}
              />
            </div>
            <div className="filter-group">
              <label>결제일자</label>
              <input
                type="date"
                name="paymentDate"
                onChange={handleFilterChange}
                value={filter.paymentDate}
              />
            </div>
          </div>
          <div className="filter-row">
            <div className="filter-group">
              <label>발행종류</label>
              <select
                name="type"
                onChange={handleFilterChange}
                value={filter.type}
              >
                <option value="tax">세금계산서</option>
                <option value="receipt">현금영수증</option>
              </select>
            </div>
            <div className="filter-group">
              <label>결제 수단</label>
              <select
                name="paymentType"
                onChange={handleFilterChange}
                value={filter.paymentType}
              >
                <option value="">전체</option>
                <option value="account">계좌이체</option>
                <option value="v_bank">가상계좌</option>
                <option value="point">포인트결제</option>
              </select>
            </div>
            <div className="filter-group">
              <label>발행상태</label>
              <select
                name="issueStatus"
                onChange={handleFilterChange}
                value={filter.issueStatus}
              >
                <option value="">전체</option>
                <option value="0">발행대기</option>
                <option value="1">발행완료</option>
                <option value="2">수정발행</option>
              </select>
            </div>
            {filter.type === "tax" ? (
              <>
                <div className="filter-group" style={{ gridColumn: "span 2" }}>
                  <label>사업자 번호</label>
                  <input
                    type="text"
                    name="businessNo"
                    onChange={handleFilterChange}
                    value={filter.businessNo}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") applyFilter();
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="filter-group">
                  <label>종류</label>
                  <select
                    name="receiptPurpose"
                    onChange={handleFilterChange}
                    value={filter.receiptPurpose}
                  >
                    <option value="">전체</option>
                    <option value="0">소득공제</option>
                    <option value="1">지출증빙</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>발급수단번호</label>
                  <input
                    type="text"
                    name="receiptIssueNumber"
                    onChange={handleFilterChange}
                    value={filter.receiptIssueNumber}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") applyFilter();
                    }}
                  />
                </div>
              </>
            )}
            <div className="filter-group">
              <button onClick={applyFilter}>조회</button>
            </div>
          </div>
        </div>
        <div className="table-header">
          <h3 className="section-title">계산서/영수증 신청 목록</h3>
          <div
            className="button-group"
            style={{
              pointerEvents: isLoading ? "none" : "auto",
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            <button
              className="list-export-btn"
              onClick={handleListExcelDownload}
              disabled={!getExcelDateRange(filter) || isListDownloading}
              style={{
                minWidth: 140,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: 4,
                padding: "8px 12px",
                fontSize: 14,
                cursor: "pointer",
                color: "#495057",
                opacity:
                  !getExcelDateRange(filter) || isListDownloading ? 0.5 : 1,
              }}
            >
              {isListDownloading ? (
                <FaSpinner className="spinner" />
              ) : (
                <FaFileExcel />
              )}
              {isListDownloading ? "다운로드 중..." : "목록 내보내기"}
            </button>
            <button
              className="export-btn"
              onClick={exportToExcel}
              disabled={isLoading}
              style={{
                minWidth: 140,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              {isLoading ? <FaSpinner className="spinner" /> : <FaFileExcel />}
              {isLoading ? "요청 중..." : "홈택스용 내보내기"}
            </button>
            <button
              className="status-btn issued"
              onClick={() => handleIssueStatus(1)}
              style={{
                minWidth: 140,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              <FaCheckCircle /> 발행완료
            </button>
            <button
              className="status-btn terminated"
              onClick={() => handleIssueStatus(-1)}
              style={{
                minWidth: 140,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              <FaTimesCircle /> 취소발행
            </button>
            <button
              className="status-btn delete"
              onClick={handleDeleteClick}
              disabled={isLoading}
              style={{
                minWidth: 140,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                backgroundColor: "#dc3545",
                color: "white",
                border: "none",
              }}
            >
              <FaTrash /> 삭제
            </button>
          </div>
        </div>
        <div className="ad-table">
          <table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={
                      invoices.length > 0 &&
                      selectedInvoices.length === invoices.length
                    }
                  />
                </th>
                <th>번호</th>
                <th>작성자</th>
                <th>결제일자</th>
                {filter.type === "tax" ? (
                  <>
                    <th>사업자번호</th>
                    <th>상호명</th>
                    <th>대표자명</th>
                  </>
                ) : (
                  <>
                    <th>발급수단번호</th>
                    <th>종류</th>
                  </>
                )}
                <th>공급가액</th>
                <th>세액</th>
                <th>합계액</th>
                <th>발행상태</th>
                <th>발행일자</th>
                <th>메모</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv, idx) => (
                <tr key={inv.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedInvoices.includes(inv.id)}
                      onChange={() => handleCheckboxChange(inv.id)}
                    />
                  </td>
                  <td>{idx + 1}</td>
                  <td>{inv.userName}</td>
                  <td>{inv.paymentDate}</td>
                  {filter.type === "tax" ? (
                    <>
                      <td>{inv.businessNo}</td>
                      <td>{inv.companyName}</td>
                      <td>{inv.ownerName}</td>
                    </>
                  ) : (
                    <>
                      <td>{inv.receiptIssueNumber}</td>
                      <td>
                        {inv.receiptPurpose === "0" ? "소득공제" : "지출증빙"}
                      </td>
                    </>
                  )}
                  <td>{formatAmount(inv.supplyAmount)}</td>
                  <td>{formatAmount(inv.taxAmount)}</td>
                  <td>{formatAmount(inv.totalAmount)}</td>
                  <td>
                    <span
                      className={`badge ${getIssueStatusClass(inv.issueStatus)}`}
                    >
                      {getIssueStatusText(inv.issueStatus)}
                    </span>
                  </td>
                  <td>{inv.issueDate}</td>
                  <td>
                    <button
                      type="button"
                      className={inv.memo ? "memo-btn has-memo" : "memo-btn"}
                      data-action="memo"
                      data-id={inv.id}
                      data-memo={inv.memo || ""}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {inv.memo ? (
                        <span className="memo-preview">{inv.memo}</span>
                      ) : (
                        "메모"
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="pagination-info">
          <div>
            총 {summary.totalCount}개 항목 중 {startIndex} - {endIndex}
          </div>
          <div className="pagination">
            <button onClick={handlePrevPage} disabled={filter.page === 1}>
              이전
            </button>
            {Array.from(
              { length: Math.ceil(summary.totalCount / filter.limit) },
              (_, i) => (
                <button
                  key={i + 1}
                  className={filter.page === i + 1 ? "active" : ""}
                  onClick={() => handlePageClick(i + 1)}
                >
                  {i + 1}
                </button>
              ),
            )}
            <button
              onClick={handleNextPage}
              disabled={endIndex >= summary.totalCount}
            >
              다음
            </button>
          </div>
        </div>
      </div>
      <MemoModal
        isOpen={memoEdit.isOpen}
        id={memoEdit.memoData?.id}
        currentMemo={memoEdit.memoData?.currentMemo}
        loading={memoEdit.loading}
        onSave={memoEdit.save}
        onDelete={memoEdit.deleteMemo}
        onClose={memoEdit.closeModal}
      />
    </Layout>
  );
}

export default InvoiceManagement;
